// The step timeline (app.md §7), shown in the modal dialog behind the
// header's Pipeline button. Renders the workflow as clickable nodes in
// main-path order (Import Control → AP/AR Approval → Department Approval →
// Director Approval → Oversight Approval), then a fork "--<" after Oversight
// Approval (v1.31): the top branch continues the main path (Export Control →
// Closing Confirmation), the bottom branch is Manual Entry (Process C —
// dashed stub, a parallel path). Denial Review hangs off the flow past a
// solid vertical divider with no connector at all (`renderTail`) — a deny can
// arrive from any approval step, so no single connector would be honest.
// Each node shows a waiting-count badge. Only nodes the user can act on are
// lit (in the accent color) — the timeline doubles as the role indicator.
// The selected node is filled accent. (A red stale-work dot on the node was
// tried and removed 2026-06-09 — not intuitive; aging lives in the queue
// rows.)
import { Fragment } from 'react'
import { makeStyles, mergeClasses, tokens, Caption1, Tooltip } from '@fluentui/react-components'
import { ArchiveRegular, WarningFilled } from '@fluentui/react-icons'
import { TIMELINE_NODES, type NodeKey, type Role, type TimelineNode } from '../lib/constants'
import type { StuckNode } from '../lib/data'
import {
  NODE_W,
  NODE_H,
  NODE_GAP,
  NODE_PAD_Y,
  CIRCLE_C,
  CIRCLE_D,
  BRANCH_GAP,
  FORK_W,
  FORK_H,
  CONNECTOR_MIN_W,
  DIVIDER_MARGIN,
  BADGE_D,
  BADGE_TOP,
  BADGE_RIGHT,
} from '../lib/timelineLayout'

// The fork glyph and row centering depend on these fixed sizes (now imported
// from `../lib/timelineLayout`, which also derives the dialog's min width
// from them): a node is NODE_H tall with its circle's center CIRCLE_C from
// the top; the two branch rows stack with BRANCH_GAP between them.

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '4px' },
  chain: { display: 'flex', alignItems: 'flex-start' },
  // The fork layout centers the left segment against the two branch rows.
  chainCentered: { display: 'flex', alignItems: 'center' },
  branches: { display: 'flex', flexDirection: 'column', gap: `${BRANCH_GAP}px`, flexGrow: 1 },
  node: {
    display: 'flex',
    flexDirection: 'column',
    // NODE_H now leaves ~21px of slack under a one-line label (30px label box
    // vs. a 13.2px line), so the flex line must start at the top rather than
    // rely on the UA button default — CIRCLE_C = 24 (which ForkGlyph and the
    // connector `marginTop` are computed from) is only true while it does.
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: `${NODE_GAP}px`,
    width: `${NODE_W}px`,
    height: `${NODE_H}px`,
    background: 'none',
    border: 'none',
    padding: `${NODE_PAD_Y}px 0`,
    fontFamily: 'inherit',
  },
  circle: {
    position: 'relative',
    width: `${CIRCLE_D}px`,
    height: `${CIRCLE_D}px`,
    // Keep it a circle: the node is a fixed-height flex column, so a two-line
    // label (e.g. "Primary Approval") would otherwise let flex compress the
    // circle vertically into an oval. flexShrink:0 pins it at 40×40; a long
    // label uses the space below instead.
    flexShrink: 0,
    boxSizing: 'border-box',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  lit: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
  },
  selected: {
    backgroundColor: tokens.colorBrandBackground,
    border: `2px solid ${tokens.colorBrandBackground}`,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  muted: { opacity: 0.5, cursor: 'default' },
  label: { textAlign: 'center', lineHeight: 1.1 },
  labelMuted: { color: tokens.colorNeutralForeground3 },
  // Waiting count rendered inside the node circle.
  count: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, lineHeight: 1 },
  dot: { fontSize: tokens.fontSizeBase300, lineHeight: 1 },
  // Archives node shows a glyph, not a count — its queue is unbounded.
  glyph: { fontSize: '20px', display: 'flex' },
  connector: {
    height: '2px',
    flex: 1,
    minWidth: `${CONNECTOR_MIN_W}px`,
    backgroundColor: tokens.colorNeutralStroke2,
    marginTop: `${CIRCLE_C - 1}px`,
  },
  // Branch link to Denial Review: same footprint, dashed instead of solid.
  // UNREACHABLE on both render paths today — `renderRow` is only ever called
  // with branch-free arrays (`main`, `rightTop` which is itself
  // `.filter(n => !n.branch)`, and the singleton `[fork]`) — and
  // `../lib/timelineLayout`'s `timelineMinWidth` assumes exactly that: no row
  // ever renders a dashed connector, so `CONNECTOR_MIN_W` alone covers every
  // connector in the min-content calculation.
  connectorDashed: {
    height: '0px',
    flex: 1,
    minWidth: '24px',
    borderTop: `2px dashed ${tokens.colorNeutralStroke2}`,
    marginTop: `${CIRCLE_C}px`,
  },
  forkGlyph: { flexShrink: 0, color: tokens.colorNeutralStroke2 },
  // Separates the branch destinations (Denial Review) from the flow proper —
  // a deny can arrive from any approval step, so no single connector is
  // honest. Vertically spans most of the fork block (v1.31 user note).
  vDivider: {
    width: '2px',
    height: `${FORK_H - 24}px`,
    backgroundColor: tokens.colorNeutralStroke2,
    borderRadius: '1px',
    flexShrink: 0,
    margin: `0 ${DIVIDER_MARGIN}px`,
  },
  // The warning icon's positioning context. A SIBLING of the button (never a
  // child): the button carries `disabled={!lit}`, and a disabled button fires
  // no mouse events in Chrome, so a tooltip trigger inside it would never
  // open — precisely the unlit case that matters, since an internal caller
  // (who chases a frozen company) has no tier node lit. Otherwise unstyled:
  // it becomes the flex item in both `s.chain` and `s.chainCentered` and is
  // sized by the 112x84 button inside it, so the connector marginTop offsets
  // above are unaffected.
  nodeWrapper: { position: 'relative' },
  stuckTrigger: {
    position: 'absolute',
    // STRADDLES THE CIRCLE'S TOP-RIGHT EDGE, so it reads as belonging to this
    // step. `BADGE_TOP`/`BADGE_RIGHT` (`../lib/timelineLayout`) are DERIVED,
    // not guessed: the circle centres in the node at (NODE_W / 2, CIRCLE_C),
    // and the badge sits at a fixed (BADGE_DX, BADGE_DY) = (16, 12) offset
    // from that centre — a distance the test file pins at exactly CIRCLE_D / 2
    // (20), i.e. on the stroke. At today's NODE_W 112 / CIRCLE_D 40 / CIRCLE_C
    // 24 that resolves to top 3px, right 31px: the circle spans x 36-76, y
    // 4-44 (centre 56,24, radius 20), the BADGE_D 18px badge spans x 63-81, y
    // 3-21 (centre 72,12). `right: 14px` (the first attempt) put it at x
    // 88-106: entirely OFF the circle, floating in the node's own whitespace.
    // NODE_H never enters this arithmetic — only the node's WIDTH does. Because
    // these are now derived from NODE_W/CIRCLE_D/CIRCLE_C rather than
    // hardcoded, a change to any of those moves the badge instead of quietly
    // sliding it off the stroke.
    top: `${BADGE_TOP}px`,
    right: `${BADGE_RIGHT}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: `${BADGE_D}px`,
    height: `${BADGE_D}px`,
    borderRadius: '50%',
    // An opaque disc behind the glyph. It has to be there: the badge sits ON
    // the circle's stroke, and a selected node's circle is filled with the
    // brand color, which the warning foreground does not read against.
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorStatusWarningForeground1,
    cursor: 'default',
  },
  stuckIcon: { fontSize: '16px' },
})

/** Cap the named companies so the tooltip cannot grow to the full 38-company list. */
const MAX_NAMED_COMPANIES = 4

/**
 * Only the company-scoped nodes can freeze, so only they may carry the icon —
 * whatever the payload happens to contain. Archives and the internal steps have
 * no per-company staffing to remove, and the sentence ("nobody is assigned to
 * this step for …") would be nonsense on them. `ap-ar` joined the three approval
 * tiers here (issue 3, 2026-08-11) — it is the node MOST likely to carry the
 * icon, because AP/AR is mandatory and an unstaffed company freezes there
 * instead of skipping past it. Mirrors `STAFFED_NODE_KEYS` in `shared/src/dto.ts`
 * (renamed from `APPROVAL_TIER_NODE_KEYS` the same day, for the same reason).
 */
const STAFFED_NODE_KEYS: ReadonlySet<NodeKey> = new Set<NodeKey>([
  'ap-ar',
  'department',
  'director',
  'oversight',
])

/**
 * Module-private (not exported): a second export from a component file fails
 * `react-refresh/only-export-components`.
 */
function stuckTooltipText(tier: StuckNode): string {
  const invoiceWord = tier.count === 1 ? 'invoice' : 'invoices'
  const named = tier.companies.slice(0, MAX_NAMED_COMPANIES)
  const remainder = tier.companies.length - named.length
  // With one company its count IS the tier count, so the parenthesis would
  // print the same number twice — "3 invoices … for SeniorLIFE Altoona (3)".
  const namedText =
    tier.companies.length === 1
      ? tier.companies[0].company_name
      : named.map((c) => `${c.company_name} (${c.count})`).join(', ')
  const suffix = remainder > 0 ? `, and ${remainder} more` : ''
  return `${tier.count} ${invoiceWord} cannot move: nobody is assigned to this step for ${namedText}${suffix}.`
}

interface NodeProps {
  label: string
  count: number
  lit: boolean
  selected: boolean
  onSelect: () => void
  /** Archive node: show a glyph instead of a waiting count. */
  archive?: boolean
  /** Present only for one of the `STAFFED_NODE_KEYS` nodes with something stuck. */
  stuck?: StuckNode
}

function Node({ label, count, lit, selected, onSelect, archive, stuck }: NodeProps) {
  const s = useStyles()
  const circleClass = mergeClasses(s.circle, lit ? s.lit : s.muted, selected && s.selected)
  return (
    <div className={s.nodeWrapper}>
      <button
        type="button"
        className={s.node}
        onClick={lit ? onSelect : undefined}
        disabled={!lit}
        aria-pressed={selected}
      >
        <div className={circleClass}>
          {archive ? (
            <ArchiveRegular className={s.glyph} />
          ) : lit ? (
            <span className={s.count}>{count}</span>
          ) : (
            <span className={s.dot}>●</span>
          )}
        </div>
        <Caption1 className={mergeClasses(s.label, !lit && s.labelMuted)}>{label}</Caption1>
      </button>
      {stuck !== undefined && (
        <Tooltip
          content={stuckTooltipText(stuck)}
          relationship="label"
          withArrow
          positioning="above"
        >
          {/* `role="img"` is required, not decoration: `relationship="label"`
              puts an `aria-label` on this element, and `aria-label` is
              PROHIBITED on the implicit `generic` role a bare <span> carries —
              axe reports `aria-prohibited-attr` and a screen reader may drop
              the sentence entirely. `img` is the right role for an icon that
              conveys information and takes no action (hence `cursor: default`
              and no handler); `tabIndex={0}` is what makes the tooltip
              keyboard-reachable, which `docs/app.md` promises. */}
          <span role="img" tabIndex={0} className={s.stuckTrigger}>
            <WarningFilled className={s.stuckIcon} />
          </span>
        </Tooltip>
      )}
    </div>
  )
}

// "--<": a short line in from the fork's anchor node, a vertical spine
// between the two branch rows' circle centers, a solid stub into the top
// (main-path) branch and a dashed stub into the bottom (parallel) branch.
function ForkGlyph() {
  const s = useStyles()
  const mid = FORK_H / 2 - NODE_H / 2 + CIRCLE_C // anchor circle's center
  const top = CIRCLE_C
  const bottom = NODE_H + BRANCH_GAP + CIRCLE_C
  const x = FORK_W / 2
  return (
    <svg
      className={s.forkGlyph}
      width={FORK_W}
      height={FORK_H}
      viewBox={`0 0 ${FORK_W} ${FORK_H}`}
      aria-hidden="true"
    >
      <path d={`M0 ${mid} H${x}`} stroke="currentColor" strokeWidth="2" fill="none" />
      <path d={`M${x} ${top} V${bottom}`} stroke="currentColor" strokeWidth="2" fill="none" />
      <path d={`M${x} ${top} H${FORK_W}`} stroke="currentColor" strokeWidth="2" fill="none" />
      <path d={`M${x} ${bottom} H${FORK_W}`} stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" fill="none" />
    </svg>
  )
}

interface Props {
  litRoles: Set<Role>
  selected: NodeKey | 'admin' | 'dashboard' | null
  counts: Record<string, number>
  onSelect: (key: NodeKey) => void
  /** The `STAFFED_NODE_KEYS` nodes with something stuck, keyed by node key. A
      node absent here draws no warning icon — including the common case,
      nothing stuck at all. */
  stuck?: Partial<Record<string, StuckNode>>
}

export function Timeline({ litRoles, selected, counts, onSelect, stuck }: Props) {
  const s = useStyles()
  // Main path first, then any branch node (Denial Review), then the archive
  // node (rendered separately, past its own divider).
  const main = TIMELINE_NODES.filter((n) => !n.branch && !n.forkFrom && !n.archive)
  const branch = TIMELINE_NODES.filter((n) => n.branch)
  const fork = TIMELINE_NODES.find((n) => n.forkFrom)
  const archive = TIMELINE_NODES.find((n) => n.archive)
  const row = [...main, ...branch]

  const renderNode = (n: TimelineNode) => (
    <Node
      label={n.label}
      count={counts[n.key] ?? 0}
      lit={litRoles.has(n.role)}
      selected={selected === n.key}
      onSelect={() => onSelect(n.key)}
      archive={n.archive}
      stuck={STAFFED_NODE_KEYS.has(n.key) ? stuck?.[n.key] : undefined}
    />
  )

  // Denial Review + Archives both hang off the flow proper, each past a
  // vertical divider — a deny arrives from any approval step, and Archives is a
  // terminal lookup, not a sequential step.
  const renderTail = () => (
    <>
      {branch.length > 0 && <div className={s.vDivider} aria-hidden />}
      {branch.map((n) => (
        <Fragment key={n.key}>{renderNode(n)}</Fragment>
      ))}
      {archive && <div className={s.vDivider} aria-hidden />}
      {archive && renderNode(archive)}
    </>
  )

  const renderRow = (nodes: TimelineNode[]) => (
    <div className={s.chain}>
      {nodes.map((n, i) => {
        const isLast = i === nodes.length - 1
        // The connector leading into a branch node is dashed.
        const nextDashed = !isLast && nodes[i + 1].branch
        return (
          <Fragment key={n.key}>
            {renderNode(n)}
            {!isLast && <div className={nextDashed ? s.connectorDashed : s.connector} />}
          </Fragment>
        )
      })}
    </div>
  )

  if (!fork)
    return (
      <div className={s.root}>
        <div className={s.chain}>
          {renderRow(main)}
          {renderTail()}
        </div>
      </div>
    )

  // Fork layout: the chain splits after forkFrom — top branch continues the
  // main path, bottom branch is the fork node. Branch destinations (Denial
  // Review) sit past a vertical divider, vertically centered like the left
  // segment — a deny can come from any approval step, so they hang off no
  // particular node.
  const anchorIdx = row.findIndex((n) => n.key === fork.forkFrom)
  const left = row.slice(0, anchorIdx + 1)
  const rightTop = row.slice(anchorIdx + 1).filter((n) => !n.branch)

  return (
    <div className={s.root}>
      <div className={s.chainCentered}>
        {left.map((n, i) => (
          <Fragment key={n.key}>
            {renderNode(n)}
            {i < left.length - 1 && (
              // The centered row is FORK_H tall; pin the connector to the
              // left nodes' circle-center height rather than the row middle.
              <div
                className={s.connector}
                style={{ alignSelf: 'flex-start', marginTop: (FORK_H - NODE_H) / 2 + CIRCLE_C - 1 }}
              />
            )}
          </Fragment>
        ))}
        <ForkGlyph />
        <div className={s.branches}>
          {renderRow(rightTop)}
          {renderRow([fork])}
        </div>
        {renderTail()}
      </div>
    </div>
  )
}
